import React, { useCallback, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { PiCloudArrowUpLight, PiWarningCircleLight, PiXLight } from 'react-icons/pi';
import { validateFileSecure } from '../../../utils/security/fileSecurityValidator';
import FileIcon from './fileIcon';
import { formatFileSize } from './attachmentModel';
import { MAX_UPLOAD_BYTES } from '../../../utils/uploadLimits';

/**
 * THE dropzone.
 *
 * Before this there were four: a `bg-white` bordered box in the task tab, a
 * staged-file control in the matter task panel, a bare `<input type="file">`
 * behind a button on the contact page, and the review playbook's own. They
 * accepted different extensions, reported errors differently (one silently
 * dropped oversized files), and only one of them ran the security validator.
 *
 * One glass surface, one validation path, one progress affordance. Callers
 * bring only `onUpload` — their own endpoint — and the zone owns everything
 * a user sees while the bytes are in flight.
 */

export interface AttachmentDropzoneProps {
  /**
   * Called with the accepted files. Resolve when the upload finished and
   * reject to surface an error — the zone drives its per-file state off the
   * returned promise, so an `async` handler is what makes progress honest.
   */
  onUpload: (files: File[]) => void | Promise<void>;
  /** Allowed extensions without dots. Defaults to the document whitelist. */
  accept?: string[];
  /** Default true. When false the zone takes one file at a time. */
  multiple?: boolean;
  maxSizeBytes?: number;
  disabled?: boolean;
  /** Replaces the default "Drop files here" headline. */
  label?: string;
  /** Replaces the default hint line under the headline. */
  hint?: string;
  /**
   * Determinate progress by file name, 0–100. Hosts that stream uploads can
   * feed real numbers; without it a staged file shows an indeterminate bar
   * for as long as `onUpload` is pending.
   */
  progress?: Record<string, number>;
  /** Compact spacing for a narrow surface pane. */
  density?: 'canvas' | 'pane';
  className?: string;
  'data-tour-id'?: string;
}

const DEFAULT_EXTENSIONS = ['pdf', 'doc', 'docx', 'txt', 'msg', 'xls', 'xlsx', 'csv', 'ppt', 'pptx'];
const DEFAULT_MAX_BYTES = MAX_UPLOAD_BYTES;

interface StagedFile {
  key: string;
  file: File;
  status: 'uploading' | 'error';
  error?: string;
}

const AttachmentDropzone: React.FC<AttachmentDropzoneProps> = ({
  onUpload,
  accept,
  multiple = true,
  maxSizeBytes = DEFAULT_MAX_BYTES,
  disabled = false,
  label,
  hint,
  progress,
  density = 'canvas',
  className,
  'data-tour-id': dataTourId,
}) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const inputId = useId();
  const [dragging, setDragging] = useState(false);
  const [staged, setStaged] = useState<StagedFile[]>([]);
  const [rejected, setRejected] = useState<string[]>([]);
  // Nested dragenter/dragleave from child nodes would otherwise flicker the
  // highlight off the moment the cursor crosses the icon.
  const dragDepth = useRef(0);

  const extensions = accept && accept.length ? accept.map((e) => e.replace(/^\./, '').toLowerCase()) : DEFAULT_EXTENSIONS;
  const acceptAttr = extensions.map((ext) => `.${ext}`).join(',');

  const handleFiles = useCallback(
    async (incoming: File[]) => {
      if (disabled || !incoming.length) return;
      const candidates = multiple ? incoming : incoming.slice(0, 1);

      const accepted: File[] = [];
      const errors: string[] = [];
      for (const file of candidates) {
        // Extension checks are advisory; this reads magic bytes too.
        const result = await validateFileSecure(file, extensions, maxSizeBytes);
        if (result.isValid) {
          accepted.push(result.processedFile || file);
        } else {
          errors.push(`${file.name}: ${result.errors[0] || t('File rejected')}`);
        }
      }

      setRejected(errors);
      if (!accepted.length) return;

      const batch: StagedFile[] = accepted.map((file, index) => ({
        key: `${file.name}-${file.size}-${index}-${staged.length}`,
        file,
        status: 'uploading',
      }));
      setStaged((prev) => [...prev, ...batch]);

      try {
        await onUpload(accepted);
        setStaged((prev) => prev.filter((s) => !batch.some((b) => b.key === s.key)));
      } catch (error: any) {
        const message = error?.message || t('Upload failed');
        setStaged((prev) =>
          prev.map((s) =>
            batch.some((b) => b.key === s.key) ? { ...s, status: 'error', error: message } : s
          )
        );
      }
    },
    [disabled, multiple, extensions, maxSizeBytes, onUpload, staged.length, t]
  );

  const onDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    dragDepth.current = 0;
    setDragging(false);
    if (disabled) return;
    handleFiles(Array.from(event.dataTransfer.files || []));
  };

  const compact = density === 'pane';

  return (
    <div className={clsx('flex-shrink-0', className)} data-tour-id={dataTourId}>
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
        aria-label={label || t('Upload files')}
        onClick={() => !disabled && inputRef.current?.click()}
        onKeyDown={(e) => {
          if (disabled) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
        onDragEnter={(e) => {
          e.preventDefault();
          dragDepth.current += 1;
          if (!disabled) setDragging(true);
        }}
        onDragOver={(e) => e.preventDefault()}
        onDragLeave={(e) => {
          e.preventDefault();
          dragDepth.current = Math.max(0, dragDepth.current - 1);
          if (dragDepth.current === 0) setDragging(false);
        }}
        onDrop={onDrop}
        className={clsx(
          'glass rounded-lg border border-dashed text-center cursor-pointer',
          'transition-colors duration-200 ease-slide focus:outline-none focus-visible:ring-2 focus-visible:ring-champagne/60',
          compact ? 'px-3 py-4' : 'px-4 py-8',
          dragging ? 'border-champagne bg-champagne/5' : 'border-hairline hover:border-champagne/60',
          disabled && 'opacity-50 cursor-not-allowed'
        )}
      >
        <PiCloudArrowUpLight
          className={clsx(
            'mx-auto text-fg-muted',
            compact ? 'h-5 w-5 mb-1.5' : 'h-7 w-7 mb-2',
            dragging && 'text-champagne'
          )}
        />
        <p className={clsx('text-fg-body', compact ? 'text-xs' : 'text-sm')}>
          {label || (multiple ? t('Drop files here or click to browse') : t('Drop a file here or click to browse'))}
        </p>
        <p className={clsx('text-fg-muted mt-1', compact ? 'text-[11px]' : 'text-xs')}>
          {hint ||
            t('{{types}} · up to {{size}}', {
              types: extensions.map((e) => e.toUpperCase()).join(', '),
              size: formatFileSize(maxSizeBytes),
            })}
        </p>
        <input
          id={inputId}
          ref={inputRef}
          type="file"
          className="hidden"
          multiple={multiple}
          accept={acceptAttr}
          disabled={disabled}
          onChange={(e) => {
            handleFiles(Array.from(e.target.files || []));
            // Same file twice in a row must still fire a change event.
            e.target.value = '';
          }}
        />
      </div>

      {staged.length > 0 && (
        <ul className="mt-2 space-y-1.5">
          {staged.map((item) => {
            const pct = progress?.[item.file.name];
            return (
              <li
                key={item.key}
                className="flex items-center gap-2 rounded-md bg-surface-sunken/70 border border-hairline-soft px-2.5 py-2"
              >
                <FileIcon name={item.file.name} mime={item.file.type} className="h-4 w-4 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-fg-body truncate">{item.file.name}</span>
                    <span className="text-[11px] text-fg-muted flex-shrink-0">
                      {formatFileSize(item.file.size)}
                    </span>
                  </div>
                  {item.status === 'uploading' ? (
                    <div className="mt-1 h-1 rounded-full bg-surface-sunken overflow-hidden">
                      <div
                        className={clsx(
                          'h-full bg-champagne rounded-full',
                          pct === undefined && 'animate-pulse w-1/3'
                        )}
                        style={pct === undefined ? undefined : { width: `${Math.min(100, Math.max(0, pct))}%` }}
                      />
                    </div>
                  ) : (
                    <p className="mt-0.5 text-[11px] text-danger-600 dark:text-danger-400 truncate">
                      {item.error}
                    </p>
                  )}
                </div>
                {item.status === 'error' && (
                  <button
                    type="button"
                    aria-label={t('Dismiss')}
                    onClick={() => setStaged((prev) => prev.filter((s) => s.key !== item.key))}
                    className="p-1 rounded-md text-fg-muted hover:text-fg-strong transition-colors"
                  >
                    <PiXLight className="h-3.5 w-3.5" />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {rejected.length > 0 && (
        <div className="mt-2 flex items-start gap-2 rounded-md border border-danger-200 dark:border-danger-800/60 bg-danger-50/60 dark:bg-danger-900/20 px-2.5 py-2">
          <PiWarningCircleLight className="h-4 w-4 text-danger-500 flex-shrink-0 mt-0.5" />
          <ul className="flex-1 min-w-0 space-y-0.5">
            {rejected.map((message) => (
              <li key={message} className="text-[11px] text-danger-700 dark:text-danger-300 break-words">
                {message}
              </li>
            ))}
          </ul>
          <button
            type="button"
            aria-label={t('Dismiss')}
            onClick={() => setRejected([])}
            className="p-1 rounded-md text-danger-500 hover:text-danger-700 transition-colors"
          >
            <PiXLight className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
    </div>
  );
};

export default AttachmentDropzone;
